import React, {useEffect, useMemo, useRef, useState} from 'react'
import {useNavigate} from 'react-router-dom'
import {toast} from 'react-toastify'
import {useAuth} from '../../../modules/auth'
import {getClient} from '../../../../components/requests'
import {
  ModeleSavClient,
  ModeleSavReferentiel,
  SavDoublon,
  SavLigneSaisie,
  SavOrigineResultat,
} from '../../../../components/sav/Models'
import {
  createSavDossier,
  envoyerSavMessageAutomatique,
  getSavAchatsClient,
  getSavLignesVente,
  getSavPieceParCode,
  savErreur,
  uploadSavDocument,
  verifierSavDoublons,
} from '../../../../components/sav/requests'
import {formatSavDate, nomSavClient, SAV_ORIGINES} from '../../../../components/sav/libelles'
import SavAc10 from '../components/SavAc10'
import SavClientRecherche from '../components/SavClientRecherche'
import SavClientCreateModal from '../components/SavClientCreateModal'
import SavScanInput from '../components/SavScanInput'
import {useSavReferentiels} from '../components/useSavReferentiels'
import SavVenteHorsKoenaModal, {SavVenteHorsKoena} from './SavVenteHorsKoenaModal'

type Vente = {type: 'koena'; achat: SavOrigineResultat} | {type: 'hors'; vente: SavVenteHorsKoena}

const maintenantLocal = () => {
  const d = new Date()
  d.setMinutes(d.getMinutes() - d.getTimezoneOffset())
  return d.toISOString().slice(0, 16)
}

let compteurLignes = 0
const cle = () => `l${++compteurLignes}`

// Champs de source vides, pour réinitialiser au changement de source
const SOURCE_VIDE = {
  appelNumero: '',
  interlocuteur: '',
  dateAppel: '',
  mailBoite: '',
  mailExpediteur: '',
  mailObjet: '',
  mailContenu: '',
  siteComptoir: '',
  pieceRapportee: false,
  plateforme: '',
  refCommandePlateforme: '',
  messagePlateforme: '',
  commentaireSource: '',
}

// Lecture minimale d'un fichier .eml : expéditeur, objet et corps texte
const lireEml = (texte: string) => {
  const [entetes, ...corps] = texte.split(/\r?\n\r?\n/)
  const entete = (nom: string) =>
    entetes
      .match(new RegExp(`^${nom}:\\s*(.*(?:\\r?\\n[ \\t].*)*)`, 'im'))?.[1]
      ?.replace(/\r?\n[ \t]+/g, ' ')
      .trim()
  const expediteur = entete('From') ?? ''
  return {
    mailExpediteur: expediteur.match(/<([^>]+)>/)?.[1] ?? expediteur,
    mailObjet: entete('Subject') ?? '',
    mailContenu: corps
      .join('\n\n')
      .replace(/<[^>]+>/g, '')
      .trim()
      .slice(0, 20000),
  }
}

// Écran 2 : saisie d'une demande SAV
const SavSaisiePage = () => {
  const navigate = useNavigate()
  const {currentUser} = useAuth()
  const referentiels = useSavReferentiels()
  const centres = referentiels?.centres ?? []

  const [source, setSource] = useState<ModeleSavReferentiel | null>(null)
  const [champsSource, setChampsSource] = useState({...SOURCE_VIDE})
  const [client, setClient] = useState<ModeleSavClient | null>(null)
  const [contact, setContact] = useState({contactNom: '', contactEmail: '', contactTelephone: ''})
  const [creationClient, setCreationClient] = useState(false)
  const [achats, setAchats] = useState<Array<SavOrigineResultat>>([])
  const [vente, setVente] = useState<Vente | null>(null)
  const [horsKoena, setHorsKoena] = useState(false)
  const [lignes, setLignes] = useState<Array<SavLigneSaisie>>([])
  const [description, setDescription] = useState('')
  const [photos, setPhotos] = useState<Array<File>>([])
  const [mailSource, setMailSource] = useState<File | null>(null)
  const [modeContact, setModeContact] = useState<'MAIL' | 'TELEPHONE'>('TELEPHONE')
  const [centre, setCentre] = useState<string>('')
  const [assigne, setAssigne] = useState<'moi' | ''>('moi')
  const [doublons, setDoublons] = useState<Array<SavDoublon>>([])
  const [confirmation, setConfirmation] = useState('')
  const [envoi, setEnvoi] = useState(false)
  const [informer, setInformer] = useState(true)
  const champPhotos = useRef<HTMLInputElement>(null)
  const champMail = useRef<HTMLInputElement>(null)

  const comportement = source?.comportement
  const generique = !!client?.generique
  // RG15 : le centre d'une vente KOENA ou B2B est celui de la commande, non modifiable
  const achatKoena =
    vente?.type === 'koena' &&
    (vente.achat.origine === 'KOENA_CMD' || vente.achat.origine === 'KOENA_B2B')
      ? vente.achat
      : null

  // Source par défaut : la première, comportement TELEPHONE de préférence
  useEffect(() => {
    if (referentiels && !source) {
      choisirSource(
        referentiels.sources.find((s) => s.comportement === 'TELEPHONE') ??
          referentiels.sources[0] ??
          null
      )
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [referentiels])

  const choisirSource = (s: ModeleSavReferentiel | null) => {
    setSource(s)
    setChampsSource({
      ...SOURCE_VIDE,
      dateAppel: s?.comportement === 'TELEPHONE' ? maintenantLocal() : '',
    })
    setModeContact(
      s?.comportement === 'MAIL' || s?.comportement === 'PLATEFORME' ? 'MAIL' : 'TELEPHONE'
    )
  }

  const majSource = (changement: Partial<typeof SOURCE_VIDE>) =>
    setChampsSource((c) => ({...c, ...changement}))

  const choisirClient = (c: ModeleSavClient) => {
    setClient(c)
    setVente(null)
    setCentre('')
    setLignes([])
    setAchats([])
    getSavAchatsClient(c.id)
      .then(setAchats)
      .catch((e) => toast.error(savErreur(e)))
  }

  const clientCree = (id: number) => {
    setCreationClient(false)
    getClient(String(id)).then((reponse: any) => reponse?.data && choisirClient(reponse.data))
  }

  // Choix d'un achat récent : préremplissage des lignes pour une vente KOENA
  const choisirAchat = (achat: SavOrigineResultat) => {
    setVente({type: 'koena', achat})
    setCentre(achat.centre ? String(achat.centre) : '')
    if (achat.origine === 'KOENA_CMD' || achat.origine === 'KOENA_B2B') {
      getSavLignesVente(achat.origine, achat.id)
        .then((lignesVente) =>
          setLignes([
            ...lignesVente.map((l) => ({
              cle: cle(),
              coche: lignesVente.length === 1 && l.quantiteDisponible !== 0,
              commandeItem: l.commandeItem,
              catalogueCommandePiece: l.catalogueCommandePiece,
              pieceEntre: l.pieceEntre,
              libellePiece: l.libellePiece ?? 'Pièce',
              referenceConstructeur: l.referenceConstructeur,
              codePieceExterne: l.code ?? undefined,
              immatriculation: l.immatriculation,
              prixVenteHt: l.prixVenteHt,
              quantiteDisponible: l.quantiteDisponible,
              garantieMois: l.typePiece?.garantie ?? null,
              typePiece: l.typePiece?.id ?? null,
            })),
          ])
        )
        .catch((e) => toast.error(savErreur(e)))
    } else {
      setLignes([])
    }
  }

  const venteHorsKoena = (v: SavVenteHorsKoena) => {
    setVente({type: 'hors', vente: v})
    setLignes([])
    setCentre(v.centre ? String(v.centre) : '')
    setHorsKoena(false)
  }

  // Pièce hors commande : code ATEMO scanné ou saisi, rapproché d'une pièce KOENA si possible
  const ajouterPieceHorsCommande = async (code: string) => {
    const piece = await getSavPieceParCode(code).catch(() => null)
    setLignes((l) => [
      ...l,
      {
        cle: cle(),
        coche: true,
        pieceEntre: piece?.pieceEntre ?? null,
        codePieceExterne: code,
        libellePiece: piece?.libellePiece ?? '',
        referenceConstructeur: piece?.referenceConstructeur,
        typePiece: piece?.typePiece?.id ?? null,
        garantieMois: piece?.typePiece?.garantie ?? null,
      },
    ])
    if (!piece) toast.info('Pièce inconnue de KOENA : précisez le type et le libellé.')
  }

  const majLigne = (cleLigne: string, changement: Partial<SavLigneSaisie>) =>
    setLignes((l) => l.map((ligne) => (ligne.cle === cleLigne ? {...ligne, ...changement} : ligne)))

  const origine =
    vente?.type === 'koena'
      ? vente.achat.origine
      : vente?.type === 'hors'
      ? vente.vente.origine
      : null
  const dateAchat =
    vente?.type === 'koena'
      ? vente.achat.date
      : vente?.type === 'hors'
      ? vente.vente.dateAchat
      : null
  const dateAchatVerifiee =
    vente?.type === 'koena' ? true : vente?.type === 'hors' ? vente.vente.dateAchatVerifiee : false
  const reclamees = lignes.filter((l) => l.coche)

  const garantie = (ligne: SavLigneSaisie): 'sous' | 'hors' | null => {
    if (!dateAchat || !ligne.garantieMois) return null
    const fin = new Date(dateAchat)
    fin.setMonth(fin.getMonth() + ligne.garantieMois)
    return fin >= new Date() ? 'sous' : 'hors'
  }

  // RG03 : alerte doublon dès que le client et les pièces sont connus
  const signatureDoublon = JSON.stringify([
    client?.id,
    reclamees.map((l) => [l.commandeItem, l.pieceEntre, l.codePieceExterne]),
  ])
  useEffect(() => {
    if (!client) {
      setDoublons([])
      return
    }
    const minuterie = setTimeout(() => {
      verifierSavDoublons({
        client: client.id,
        refFactureExterne: vente?.type === 'hors' ? vente.vente.refFactureExterne : undefined,
        refCommandeExterne: vente?.type === 'hors' ? vente.vente.refCommandeExterne : undefined,
        lignes: reclamees.map((l) => ({
          commandeItem: l.commandeItem,
          pieceEntre: l.pieceEntre,
          codePieceExterne: l.codePieceExterne,
        })),
      })
        .then(setDoublons)
        .catch(() => setDoublons([]))
    }, 400)
    return () => clearTimeout(minuterie)
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [signatureDoublon])

  const manquants = useMemo(() => {
    const liste: Array<string> = []
    if (!source) liste.push('la source')
    if (!client) liste.push('le client')
    if (
      generique &&
      (!contact.contactNom.trim() ||
        (!contact.contactEmail.trim() && !contact.contactTelephone.trim()))
    )
      liste.push('le nom et un moyen de contact de la personne')
    if (!vente) liste.push("la vente d'origine")
    if (!reclamees.length) liste.push('au moins une pièce')
    if (reclamees.some((l) => !l.motif)) liste.push('le motif de chaque pièce')
    if (
      reclamees.some(
        (l) =>
          !l.libellePiece.trim() ||
          (!l.pieceEntre && !l.typePiece && !l.commandeItem && !l.catalogueCommandePiece)
      )
    )
      liste.push('le type et le libellé des pièces hors KOENA')
    if (reclamees.some((l) => l.quantiteDisponible === 0))
      liste.push('des pièces encore réclamables')
    if (!description.trim()) liste.push('la réclamation du client')
    if (achatKoena && !achatKoena.centre)
      liste.push(`un centre sur la commande ${achatKoena.numero} (anomalie de donnée)`)
    else if (!centre) liste.push('le centre de la vente')
    if (comportement === 'TELEPHONE' && !champsSource.appelNumero.trim())
      liste.push('le n° appelant')
    if (comportement === 'MAIL' && !champsSource.mailExpediteur.trim())
      liste.push("l'expéditeur du mail")
    if (comportement === 'COMPTOIR' && !champsSource.siteComptoir) liste.push('le comptoir')
    if (comportement === 'GENERIQUE' && !champsSource.interlocuteur.trim())
      liste.push("l'interlocuteur")
    if (doublons.length && !confirmation.trim()) liste.push('la confirmation du doublon')
    return liste
  }, [
    source,
    client,
    generique,
    contact,
    vente,
    reclamees,
    description,
    centre,
    achatKoena,
    comportement,
    champsSource,
    doublons,
    confirmation,
  ])

  const creer = async () => {
    if (!source || !client || !vente) return
    setEnvoi(true)
    const s = champsSource
    const donnees: Record<string, any> = {
      source: source.id,
      client: client.id,
      ...(generique ? contact : {}),
      modeContact,
      centre: Number(centre),
      utilisateurAssigne: assigne === 'moi' ? currentUser?.id : null,
      description,
      confirmationDoublon: doublons.length ? confirmation : undefined,
      appelNumero: s.appelNumero || undefined,
      interlocuteur: s.interlocuteur || undefined,
      dateAppel: s.dateAppel || undefined,
      mailBoite: s.mailBoite || undefined,
      mailExpediteur: s.mailExpediteur || undefined,
      mailObjet: s.mailObjet || undefined,
      mailContenu: s.mailContenu || undefined,
      siteComptoir: s.siteComptoir ? Number(s.siteComptoir) : undefined,
      pieceRapportee: comportement === 'COMPTOIR' && s.pieceRapportee,
      messagePlateforme: s.messagePlateforme || undefined,
      commentaireSource: s.commentaireSource || undefined,
      lignes: reclamees.map((l) => ({
        commandeItem: l.commandeItem,
        catalogueCommandePiece: l.catalogueCommandePiece,
        pieceEntre: l.pieceEntre ?? undefined,
        codePieceExterne:
          l.commandeItem || l.catalogueCommandePiece ? undefined : l.codePieceExterne,
        typePiece: l.typePiece ?? undefined,
        libellePiece: l.libellePiece,
        immatriculation: l.immatriculation ?? undefined,
        motif: l.motif,
      })),
    }
    if (vente.type === 'koena') {
      const a = vente.achat
      donnees.origine = a.origine
      if (a.origine === 'KOENA_CMD') donnees.commande = a.id
      if (a.origine === 'KOENA_B2B') donnees.catalogueCommande = a.id
      if (a.origine === 'ATEMO') donnees.clientHistoriqueFacturation = a.id
    } else {
      const v = vente.vente
      Object.assign(donnees, {
        origine: v.origine,
        refFactureExterne: v.refFactureExterne || undefined,
        refCommandeExterne: v.refCommandeExterne || undefined,
        plateforme: v.plateforme || undefined,
        refTicketOdoo: v.refTicketOdoo || undefined,
        dateAchat: v.dateAchat || undefined,
        dateAchatVerifiee: v.dateAchatVerifiee,
        clientHistoriqueFacturation: v.clientHistoriqueFacturation,
      })
    }
    if (
      comportement === 'PLATEFORME' &&
      vente.type === 'hors' &&
      vente.vente.origine === 'PLATEFORME'
    ) {
      donnees.plateforme ??= s.plateforme || undefined
      donnees.refCommandeExterne ??= s.refCommandePlateforme || undefined
    }

    try {
      const {dossier} = await createSavDossier(donnees)
      // Pièces jointes envoyées une fois le dossier créé
      const envois: Array<[File, string]> = [
        ...photos.map((f) => [f, 'PHOTO_CLIENT'] as [File, string]),
        ...(vente.type === 'hors'
          ? vente.vente.justificatifs.map((f) => [f, 'JUSTIFICATIF'] as [File, string])
          : []),
        ...(mailSource ? [[mailSource, 'MAIL_SOURCE'] as [File, string]] : []),
      ]
      for (const [fichier, type] of envois) {
        await uploadSavDocument(dossier.id, fichier, type).catch((e) =>
          toast.warning(`${fichier.name} : ${savErreur(e)}`)
        )
      }
      toast.success(`SAV ${dossier.numero} créé`)
      if (informer) {
        const motif = await envoyerSavMessageAutomatique(dossier.id, 'CREATION').catch((e) =>
          savErreur(e)
        )
        if (motif)
          toast.warning(
            `Mail « Création du SAV » non envoyé : ${motif}. Vous pourrez l'envoyer depuis le dossier.`
          )
      }
      navigate(
        dossier.statut.code === 'OUVERT'
          ? `/sav/dossiers/${dossier.id}/qualification`
          : `/sav/dossiers/${dossier.id}/fiche`
      )
    } catch (e) {
      toast.error(savErreur(e))
    } finally {
      setEnvoi(false)
    }
  }

  const importerMail = async (fichier?: File) => {
    if (!fichier) return
    setMailSource(fichier)
    if (fichier.name.toLowerCase().endsWith('.eml')) {
      majSource(lireEml(await fichier.text()))
    } else {
      toast.info("Fichier .msg joint au dossier : recopiez l'expéditeur et l'objet.")
    }
  }

  const champTexte = (
    libelle: string,
    cleChamp: keyof typeof SOURCE_VIDE,
    requis = false,
    type = 'text'
  ) => (
    <div className='col-md-4 mb-4'>
      <label className={`form-label ${requis ? 'required' : ''}`}>{libelle}</label>
      <input
        type={type}
        className='form-control form-control-solid'
        value={champsSource[cleChamp] as string}
        onChange={(e) => majSource({[cleChamp]: e.target.value})}
      />
    </div>
  )

  if (!referentiels) return null

  return (
    <>
      <div className='d-flex align-items-center gap-4 mb-5'>
        <button className='btn btn-sm btn-light' onClick={() => navigate('/sav/liste')}>
          <i className='bi bi-chevron-left'></i> Retour
        </button>
        <SavAc10 etape='Demande SAV' acteur='TC SAV' delai='T1' />
      </div>

      <div className='row g-5'>
        <div className='col-lg-8'>
          {/* Bloc 1 – Source */}
          <div className='card mb-5'>
            <div className='card-header'>
              <h3 className='card-title'>1. Source de la demande</h3>
            </div>
            <div className='card-body'>
              <div className='d-flex flex-wrap gap-2 mb-6'>
                {referentiels.sources.map((s) => (
                  <button
                    key={s.id}
                    type='button'
                    className={`btn ${source?.id === s.id ? 'btn-success' : 'btn-light'}`}
                    onClick={() => choisirSource(s)}
                  >
                    {s.libelle}
                  </button>
                ))}
              </div>
              <div className='row'>
                {comportement === 'TELEPHONE' && (
                  <>
                    {champTexte('N° appelant', 'appelNumero', true)}
                    {champTexte('Interlocuteur', 'interlocuteur')}
                    {champTexte("Date et heure de l'appel", 'dateAppel', false, 'datetime-local')}
                  </>
                )}
                {comportement === 'MAIL' && (
                  <>
                    <div className='col-md-4 mb-4'>
                      <label className='form-label'>Boîte de réception</label>
                      <select
                        className='form-select'
                        value={champsSource.mailBoite}
                        onChange={(e) => majSource({mailBoite: e.target.value})}
                      >
                        <option value=''>Choisir</option>
                        {referentiels.boitesMail.map((b) => (
                          <option key={b} value={b}>
                            {b}
                          </option>
                        ))}
                      </select>
                    </div>
                    {champTexte('Expéditeur', 'mailExpediteur', true, 'email')}
                    {champTexte('Objet', 'mailObjet')}
                    <div className='col-12 mb-4'>
                      <label className='form-label'>Contenu du mail</label>
                      <textarea
                        className='form-control form-control-solid'
                        rows={4}
                        value={champsSource.mailContenu}
                        onChange={(e) => majSource({mailContenu: e.target.value})}
                      />
                    </div>
                    <div className='col-12'>
                      <input
                        ref={champMail}
                        type='file'
                        accept='.eml,.msg'
                        className='d-none'
                        onChange={(e) => importerMail(e.target.files?.[0])}
                      />
                      <button
                        type='button'
                        className='btn btn-sm btn-light-primary'
                        onClick={() => champMail.current?.click()}
                      >
                        <i className='bi bi-envelope-arrow-down'></i> Importer le mail (.eml / .msg)
                      </button>
                      {mailSource && <span className='ms-3 text-muted'>{mailSource.name}</span>}
                    </div>
                  </>
                )}
                {comportement === 'COMPTOIR' && (
                  <>
                    <div className='col-md-4 mb-4'>
                      <label className='form-label required'>Comptoir</label>
                      <select
                        className='form-select'
                        value={champsSource.siteComptoir}
                        onChange={(e) => majSource({siteComptoir: e.target.value})}
                      >
                        <option value=''>Choisir</option>
                        {referentiels.sites.map((site) => (
                          <option key={site.id} value={site.id}>
                            {site.nom}
                          </option>
                        ))}
                      </select>
                    </div>
                    <div className='col-md-8 mb-4'>
                      <label className='form-label'>Le client rapporte-t-il la pièce ?</label>
                      <div className='d-flex gap-5 mt-2'>
                        {[true, false].map((valeur) => (
                          <label className='form-check form-check-inline' key={String(valeur)}>
                            <input
                              className='form-check-input'
                              type='radio'
                              checked={champsSource.pieceRapportee === valeur}
                              onChange={() => majSource({pieceRapportee: valeur})}
                            />
                            <span className='form-check-label'>{valeur ? 'Oui' : 'Non'}</span>
                          </label>
                        ))}
                      </div>
                    </div>
                    {champsSource.pieceRapportee && (
                      <div className='col-12'>
                        <div className='alert alert-warning mb-0'>
                          Pas de bon de retour : le dossier passe directement en Reçu et part au
                          contrôle labo. Le retour est enregistré automatiquement (site du comptoir,
                          date du jour, vous comme réceptionnaire).
                        </div>
                      </div>
                    )}
                  </>
                )}
                {comportement === 'PLATEFORME' && (
                  <>
                    {champTexte('Plateforme', 'plateforme', true)}
                    {champTexte('N° de commande plateforme', 'refCommandePlateforme', true)}
                    <div className='col-12'>
                      <label className='form-label'>Message du client sur la plateforme</label>
                      <textarea
                        className='form-control form-control-solid'
                        rows={3}
                        value={champsSource.messagePlateforme}
                        onChange={(e) => majSource({messagePlateforme: e.target.value})}
                      />
                    </div>
                  </>
                )}
                {comportement === 'GENERIQUE' && (
                  <>
                    {champTexte('Interlocuteur', 'interlocuteur', true)}
                    <div className='col-md-8 mb-4'>
                      <label className='form-label'>Commentaire</label>
                      <input
                        className='form-control form-control-solid'
                        value={champsSource.commentaireSource}
                        onChange={(e) => majSource({commentaireSource: e.target.value})}
                      />
                    </div>
                  </>
                )}
              </div>
            </div>
          </div>

          {/* Bloc 2 – Client */}
          <div className='card mb-5'>
            <div className='card-header'>
              <h3 className='card-title'>2. Client</h3>
            </div>
            <div className='card-body'>
              {client ? (
                <div
                  className='rounded p-4 d-flex justify-content-between'
                  style={{border: '2px dashed #50CD89'}}
                >
                  <div>
                    <div className='fs-4 fw-bolder text-uppercase'>{nomSavClient(client)}</div>
                    <div className='d-flex gap-2 my-1'>
                      {generique ? (
                        <span className='badge badge-light-warning'>Client générique</span>
                      ) : (
                        <>
                          <span className='badge badge-light-success'>CLIENT</span>
                          <span className='badge badge-light-primary'>
                            {client.professionnel ? 'Professionnel' : 'Particulier'}
                          </span>
                        </>
                      )}
                    </div>
                    {client.email && (
                      <div>
                        <i className='bi bi-envelope me-2'></i>
                        {client.email}
                      </div>
                    )}
                    {(client.telephone || client.mobile) && (
                      <div>
                        <i className='bi bi-telephone me-2'></i>
                        {client.telephone || client.mobile}
                      </div>
                    )}
                  </div>
                  <button
                    className='btn btn-sm btn-light align-self-start'
                    onClick={() => setClient(null)}
                  >
                    Changer
                  </button>
                </div>
              ) : (
                <SavClientRecherche
                  onChoix={choisirClient}
                  onCreer={() => setCreationClient(true)}
                />
              )}
              {generique && (
                <div className='row mt-4'>
                  {(
                    [
                      ['Nom de la personne', 'contactNom', 'text'],
                      ['Mail', 'contactEmail', 'email'],
                      ['Téléphone', 'contactTelephone', 'tel'],
                    ] as const
                  ).map(([libelle, champ, type]) => (
                    <div className='col-md-4' key={champ}>
                      <label className={`form-label ${champ === 'contactNom' ? 'required' : ''}`}>
                        {libelle}
                      </label>
                      <input
                        type={type}
                        className='form-control form-control-solid'
                        value={contact[champ]}
                        onChange={(e) => setContact((c) => ({...c, [champ]: e.target.value}))}
                      />
                    </div>
                  ))}
                  <div className='text-muted fs-7 mt-2'>
                    Le nom et au moins un moyen de contact sont obligatoires ; ils servent aux
                    messages et à la création de la fiche plus tard.
                  </div>
                </div>
              )}
              {doublons.length > 0 && (
                <div className='alert alert-warning mt-4 mb-0'>
                  <div>
                    {doublons.length} SAV en cours pour ce client :{' '}
                    {doublons.map((d) => `${d.numero} (${d.statut})`).join(', ')}. Vérifiez qu'il ne
                    s'agit pas d'un doublon.
                  </div>
                  <input
                    className='form-control mt-3'
                    placeholder='Commentaire de confirmation (obligatoire pour créer quand même)'
                    value={confirmation}
                    onChange={(e) => setConfirmation(e.target.value)}
                  />
                </div>
              )}
            </div>
          </div>

          {/* Bloc 3 – Vente d'origine */}
          <div className='card mb-5'>
            <div className='card-header'>
              <h3 className='card-title'>3. Vente d'origine</h3>
            </div>
            <div className='card-body'>
              {!client ? (
                <div className='text-muted'>Choisissez d'abord le client.</div>
              ) : (
                <>
                  <div className='list-group mb-3'>
                    {achats.map((achat) => {
                      const actif =
                        vente?.type === 'koena' &&
                        vente.achat.origine === achat.origine &&
                        vente.achat.id === achat.id
                      return (
                        <label
                          key={`${achat.origine}-${achat.id}`}
                          className={`list-group-item d-flex gap-3 align-items-center ${
                            actif ? 'bg-light-success' : ''
                          }`}
                        >
                          <input
                            type='radio'
                            className='form-check-input mt-0'
                            checked={actif}
                            onChange={() => choisirAchat(achat)}
                          />
                          <span className='fw-bold'>{SAV_ORIGINES[achat.origine]}</span>
                          <span>{achat.numero}</span>
                          <span className='text-muted'>{formatSavDate(achat.date)}</span>
                          {achat.nbPieces !== null && (
                            <span className='text-muted'>{achat.nbPieces} pièce(s)</span>
                          )}
                        </label>
                      )
                    })}
                    <label
                      className={`list-group-item d-flex gap-3 align-items-center ${
                        vente?.type === 'hors' ? 'bg-light-success' : ''
                      }`}
                    >
                      <input
                        type='radio'
                        className='form-check-input mt-0'
                        checked={vente?.type === 'hors'}
                        onChange={() => setHorsKoena(true)}
                      />
                      <span className='fw-bold'>Vente introuvable</span>
                      {vente?.type === 'hors' && (
                        <span className='text-muted'>
                          {SAV_ORIGINES[vente.vente.origine]}{' '}
                          {vente.vente.refFactureExterne ||
                            vente.vente.refCommandeExterne ||
                            vente.vente.refTicketOdoo}
                        </span>
                      )}
                    </label>
                  </div>
                  {!achats.length && (
                    <div className='text-muted mb-3'>Aucun achat sur les 24 derniers mois.</div>
                  )}
                  <button
                    type='button'
                    className='btn btn-link p-0 text-success fw-bold'
                    onClick={() => setHorsKoena(true)}
                  >
                    Vente introuvable dans KOENA ?
                  </button>
                  {vente?.type === 'hors' && (
                    <div className='text-muted fs-7 mt-2'>
                      Après la création, le bouton « Demander la référence au client » de l'écran de
                      qualification envoie le message correspondant.
                    </div>
                  )}
                </>
              )}
            </div>
          </div>

          {/* Bloc 4 – Pièces */}
          <div className='card mb-5'>
            <div className='card-header'>
              <h3 className='card-title'>4. Pièces réclamées</h3>
            </div>
            <div className='card-body'>
              <div className='table-responsive'>
                <table className='table align-middle gs-0 gy-3'>
                  <thead>
                    <tr className='fw-bold text-muted'>
                      <th></th>
                      <th>Référence pièce</th>
                      <th>Immatriculation</th>
                      <th>Garantie</th>
                      <th>Motif</th>
                    </tr>
                  </thead>
                  <tbody>
                    {lignes.length ? (
                      lignes.map((l) => {
                        const g = garantie(l)
                        const epuisee = l.quantiteDisponible === 0
                        return (
                          <tr
                            key={l.cle}
                            style={l.coche ? {backgroundColor: '#FFFDF0'} : undefined}
                          >
                            <td>
                              <input
                                type='checkbox'
                                className='form-check-input'
                                checked={l.coche}
                                disabled={epuisee}
                                onChange={(e) => majLigne(l.cle, {coche: e.target.checked})}
                              />
                            </td>
                            <td>
                              {l.commandeItem || l.catalogueCommandePiece || l.pieceEntre ? (
                                <span className='fw-bold'>
                                  {l.codePieceExterne ? `${l.codePieceExterne} - ` : ''}
                                  {l.libellePiece}
                                </span>
                              ) : (
                                <div className='d-flex gap-2'>
                                  <span className='text-muted align-self-center text-nowrap'>
                                    {l.codePieceExterne}
                                  </span>
                                  <select
                                    className='form-select form-select-sm'
                                    value={l.typePiece ?? ''}
                                    onChange={(e) => {
                                      const type = referentiels.typesPiece.find(
                                        (t) => t.id === Number(e.target.value)
                                      )
                                      majLigne(l.cle, {
                                        typePiece: type?.id ?? null,
                                        garantieMois: type?.garantie ?? null,
                                        libellePiece: l.libellePiece || type?.nom || '',
                                      })
                                    }}
                                  >
                                    <option value=''>Type de pièce</option>
                                    {referentiels.typesPiece.map((t) => (
                                      <option key={t.id} value={t.id}>
                                        {t.nom}
                                      </option>
                                    ))}
                                  </select>
                                  <input
                                    className='form-control form-control-sm'
                                    placeholder='Libellé'
                                    value={l.libellePiece}
                                    onChange={(e) =>
                                      majLigne(l.cle, {libellePiece: e.target.value})
                                    }
                                  />
                                </div>
                              )}
                              {l.referenceConstructeur && (
                                <div className='text-muted fs-7'>
                                  Réf. OE {l.referenceConstructeur}
                                </div>
                              )}
                              {epuisee && (
                                <div className='text-danger fs-7'>
                                  Déjà réclamée dans un autre SAV
                                </div>
                              )}
                            </td>
                            <td>
                              <input
                                className='form-control form-control-sm w-125px'
                                value={l.immatriculation ?? ''}
                                onChange={(e) => majLigne(l.cle, {immatriculation: e.target.value})}
                              />
                            </td>
                            <td>
                              {g === 'sous' && (
                                <span className='badge badge-light-success'>
                                  Sous garantie{!dateAchatVerifiee && ' (déclarative)'}
                                </span>
                              )}
                              {g === 'hors' && (
                                <span className='badge badge-light-danger'>Hors garantie</span>
                              )}
                              {!g && <span className='text-muted fs-7'>—</span>}
                            </td>
                            <td>
                              <select
                                className='form-select form-select-sm'
                                disabled={!l.coche}
                                value={l.motif ?? ''}
                                onChange={(e) =>
                                  majLigne(l.cle, {
                                    motif: e.target.value ? Number(e.target.value) : undefined,
                                  })
                                }
                              >
                                <option value=''>Motif</option>
                                {referentiels.motifs.map((m) => (
                                  <option key={m.id} value={m.id}>
                                    {m.libelle}
                                  </option>
                                ))}
                              </select>
                            </td>
                          </tr>
                        )
                      })
                    ) : (
                      <tr>
                        <td colSpan={5} className='text-muted text-center'>
                          Choisissez une vente KOENA, ou ajoutez une pièce hors commande.
                        </td>
                      </tr>
                    )}
                  </tbody>
                </table>
              </div>
              <div className='mb-5'>
                <label className='form-label fw-bold'>+ Pièce hors commande (code ATEMO)</label>
                <SavScanInput
                  placeholder='Scanner ou saisir le code pièce'
                  onScan={ajouterPieceHorsCommande}
                />
              </div>
              <label className='form-label required'>Réclamation du client (ses mots)</label>
              <textarea
                className='form-control form-control-solid mb-4'
                rows={3}
                value={description}
                onChange={(e) => setDescription(e.target.value)}
              />
              <input
                ref={champPhotos}
                type='file'
                multiple
                accept='image/*'
                className='d-none'
                onChange={(e) => setPhotos((p) => [...p, ...Array.from(e.target.files ?? [])])}
              />
              <button
                type='button'
                className='btn btn-sm btn-light-primary'
                onClick={() => champPhotos.current?.click()}
              >
                <i className='bi bi-camera'></i> Ajouter des photos
              </button>
              {photos.length > 0 && (
                <span className='ms-3 text-muted'>
                  {photos.length} photo(s){' '}
                  <button
                    type='button'
                    className='btn btn-link p-0 ms-1'
                    onClick={() => setPhotos([])}
                  >
                    retirer
                  </button>
                </span>
              )}
            </div>
          </div>
        </div>

        {/* Bloc 5 – Suite à donner */}
        <div className='col-lg-4'>
          <div className='card' style={{position: 'sticky', top: 90}}>
            <div className='card-header'>
              <h3 className='card-title'>5. Suite à donner</h3>
            </div>
            <div className='card-body'>
              <label className='form-label'>Mode de contact souhaité</label>
              <select
                className='form-select mb-4'
                value={modeContact}
                onChange={(e) => setModeContact(e.target.value as 'MAIL' | 'TELEPHONE')}
              >
                <option value='TELEPHONE'>Téléphone</option>
                <option value='MAIL'>Mail</option>
              </select>
              <label className='form-label required'>Centre de la vente</label>
              {achatKoena ? (
                <>
                  <input
                    className='form-control'
                    disabled
                    value={achatKoena.centreLibelle ?? ''}
                    placeholder='Centre introuvable'
                  />
                  <div className={`form-text mb-4 ${achatKoena.centre ? '' : 'text-danger'}`}>
                    {achatKoena.centre
                      ? `Issu de la commande ${achatKoena.numero}`
                      : `Centre introuvable sur la commande ${achatKoena.numero} : signalez l'anomalie de donnée.`}
                  </div>
                </>
              ) : (
                <>
                  <select
                    className='form-select'
                    value={centre}
                    onChange={(e) => setCentre(e.target.value)}
                  >
                    <option value=''>Choisir</option>
                    {centres.map((c) => (
                      <option key={c.id} value={c.id}>
                        {c.libelle}
                      </option>
                    ))}
                  </select>
                  <div className='form-text mb-4'>Centre qui a vendu la pièce</div>
                </>
              )}
              <label className='form-label'>Assigner à</label>
              <select
                className='form-select mb-5'
                value={assigne}
                onChange={(e) => setAssigne(e.target.value as 'moi' | '')}
              >
                <option value='moi'>Moi</option>
                <option value=''>Interlocuteur SAV (à assigner)</option>
              </select>

              <div className='bg-light rounded p-4 mb-5'>
                <div className='d-flex justify-content-between'>
                  <span className='text-muted'>Source</span>
                  <span className='fw-bold'>{source?.libelle ?? '—'}</span>
                </div>
                <div className='d-flex justify-content-between'>
                  <span className='text-muted'>Client</span>
                  <span className='fw-bold text-end'>
                    {client ? nomSavClient(client, contact.contactNom) : '—'}
                  </span>
                </div>
                <div className='d-flex justify-content-between'>
                  <span className='text-muted'>Vente</span>
                  <span className='fw-bold'>{origine ? SAV_ORIGINES[origine] : '—'}</span>
                </div>
                <div className='d-flex justify-content-between'>
                  <span className='text-muted'>Pièces réclamées</span>
                  <span className='fw-bold'>{reclamees.length}</span>
                </div>
                <div className='d-flex justify-content-between'>
                  <span className='text-muted'>Statut à la création</span>
                  <span className='fw-bold'>
                    {comportement === 'COMPTOIR' && champsSource.pieceRapportee
                      ? 'Reçu (pièce rapportée)'
                      : 'Ouvert'}
                  </span>
                </div>
              </div>
              <label className='form-check form-check-custom form-check-solid mb-4'>
                <input
                  className='form-check-input'
                  type='checkbox'
                  checked={informer}
                  onChange={(e) => setInformer(e.target.checked)}
                />
                <span className='form-check-label'>Envoyer le n° SAV au client par mail</span>
              </label>
              {manquants.length > 0 && (
                <div className='text-muted fs-7 mb-3'>À compléter : {manquants.join(', ')}.</div>
              )}
              <button
                className='btn btn-success w-100'
                disabled={manquants.length > 0 || envoi}
                onClick={creer}
              >
                {envoi ? 'Création…' : 'Créer le SAV'}
              </button>
            </div>
          </div>
        </div>
      </div>

      {creationClient && (
        <SavClientCreateModal onClose={() => setCreationClient(false)} onCree={clientCree} />
      )}
      {horsKoena && client && (
        <SavVenteHorsKoenaModal
          nomClient={nomSavClient(client)}
          centres={centres}
          initiale={
            vente?.type === 'hors'
              ? vente.vente
              : comportement === 'PLATEFORME'
              ? {
                  origine: 'PLATEFORME',
                  plateforme: champsSource.plateforme,
                  refCommandeExterne: champsSource.refCommandePlateforme,
                  dateAchatVerifiee: false,
                  justificatifs: [],
                }
              : null
          }
          onClose={() => setHorsKoena(false)}
          onRattacher={venteHorsKoena}
        />
      )}
    </>
  )
}

export default SavSaisiePage
